/**
 * NotificationCentre — the speech-bubble icon in the title bar, and the panel
 * of history behind it.
 *
 * THE ICON, per Andy's spec: OUTLINE when there is nothing unread, SOLID and
 * PULSING when there is. The pulse is the whole point — a count badge alone is
 * easy to walk past, and an approval waiting on you is time-limited. It is also
 * why the pulse animation is disabled under `prefers-reduced-motion`: the
 * information has to survive without the movement, so the solid icon and the
 * badge carry it on their own.
 *
 * Distinct from the three feature bells beside it (Security, Network,
 * Forcepoint), which are live indicators that reset when you look at them and
 * keep no history. This one is server-backed and durable: it is the record of
 * what happened, not a light that goes out.
 *
 * Clicking a notification does what the notification is ABOUT — an approval
 * opens the approve modal — which is why the store keeps a structured `link`
 * rather than a URL.
 */

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNotificationsStore } from '@/state/notificationsStore.js';
import { useUiStore, isValidTab } from '@/state/uiStore.js';
import ApprovalModal from '@/components/approvals/ApprovalModal.jsx';
// Module-level, not the useToast() hook: the hook returns a fresh object every
// render, so an effect depending on it would tear down and re-create the poll
// interval continuously.
import { showToast } from '@/components/ui/useToast.js';
import { URGENT_NOTIFICATION_KINDS } from '@omni-itm/shared';
import UrgentAlertModal, { TONE_COLOUR, alertStatusText, playAlertChime, playRedAlert } from './UrgentAlertModal.jsx';
import { desktopPushState, disableDesktopPush, enableDesktopPush, syncDesktopPush } from '@/utils/desktopPush.js';
import { claimAlert, fetchAlerts, sendTestPush } from '@/api/endpoints/notifications.js';

const STATUS_COLOUR = TONE_COLOUR;

/**
 * One line about the reader's own place in the on-call routing, so nobody is
 * surprised to be woken, or to be skipped.
 */
function onCallLine(overview) {
  if (!overview?.enabled || !overview.onCall) return null;
  const { onCall, you } = overview;
  if (you?.outOfOffice) {
    return { tone: 'muted', text: 'Your Outlook out-of-office is on, so critical alerts skip you.' };
  }
  if (onCall.youAreOnCall) {
    return you?.hasAlertDevice
      ? { tone: 'ok', text: `You're on call. Critical alerts come to you first; everyone is told after ${overview.escalationMinutes} min if nobody claims them.` }
      : { tone: 'bad', text: "You're on call but desktop alerts are off, so critical alerts go straight to everyone. Turn them on below." };
  }
  if (!onCall.configured) return null;
  if (!onCall.readable) return { tone: 'muted', text: "Couldn't read the on-call group, so critical alerts go to everyone." };
  return onCall.count > 0
    ? { tone: 'muted', text: `${onCall.count} person${onCall.count === 1 ? ' is' : 's are'} on call. You're told about critical alerts if nobody claims them within ${overview.escalationMinutes} min.` }
    : { tone: 'muted', text: 'Nobody is on call right now, so critical alerts go to everyone.' };
}

/** Shown under the panel header so everyone can see, and change, whether alerts reach their desktop. */
const PUSH_STATE_TEXT = {
  on: 'Desktop alerts are ON for this browser — urgent alerts arrive even with Omni closed.',
  off: 'Desktop alerts are off — you only hear about urgent alerts while Omni is open.',
  blocked: 'Notifications are blocked for Omni in this browser. Allow them in site settings (padlock in the address bar).',
  unsupported: 'Desktop alerts are not available here (private window, or not the installed live site).',
  unknown: 'Checking desktop alerts\u2026'
};

function relativeTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.round(hours / 24);
  return days === 1 ? 'yesterday' : `${days} days ago`;
}

/**
 * Where a notification takes you.
 *
 * An explicit `link.tab` wins, so the server can send somebody to any tab
 * without this file needing to know what the notification was about. The rest
 * is a fallback for the link types that name a THING rather than a place.
 *
 * Always validated with `isValidTab`: `setActiveTab` only warns and does
 * nothing for an unknown id, so an unchecked value here would render a row that
 * looks clickable and then does nothing when pressed.
 */
const LINK_TYPE_TAB = {
  user: 'user-management-v2',
  'change-record': 'user-management-v2'
};

/** An external page to open (a ServiceDesk ticket), https only. */
function externalUrl(link) {
  const url = link?.url;
  return typeof url === 'string' && /^https:\/\//i.test(url) ? url : null;
}

function destinationTab(link) {
  if (!link) return null;
  const candidate = link.tab || LINK_TYPE_TAB[link.type];
  return candidate && isValidTab(candidate) ? candidate : null;
}

/**
 * One entry per NotificationKind. A kind missing from here falls back to the
 * generic `info` look, which is a silent downgrade rather than an error — so
 * adding a kind server-side means adding it here too.
 */
const KIND_ICON = {
  'approval-requested': { icon: 'fa-user-shield', colour: '#b45309' },
  'approval-granted': { icon: 'fa-circle-check', colour: '#047857' },
  'approval-rejected': { icon: 'fa-circle-xmark', colour: '#b91c1c' },
  'approval-expired': { icon: 'fa-clock', colour: '#4b5563' },
  'network-incident': { icon: 'fa-network-wired', colour: '#b91c1c' },
  'network-restored': { icon: 'fa-network-wired', colour: '#047857' },
  'security-incident': { icon: 'fa-shield-halved', colour: '#b91c1c' },
  'security-alert': { icon: 'fa-triangle-exclamation', colour: '#b45309' },
  'web-filtering-blocks': { icon: 'fa-globe', colour: '#b45309' },
  'backup-failed': { icon: 'fa-database', colour: '#b91c1c' },
  'risky-user': { icon: 'fa-user-secret', colour: '#b91c1c' },
  'servicedesk-priority': { icon: 'fa-ticket', colour: '#b91c1c' },
  'servicedesk-assigned': { icon: 'fa-ticket', colour: '#4f46e5' },
  'service-notice': { icon: 'fa-bullhorn', colour: '#b45309' },
  'servicedesk-updated': { icon: 'fa-comment-dots', colour: '#4f46e5' },
  'servicedesk-group-assigned': { icon: 'fa-people-group', colour: '#4f46e5' },
  info: { icon: 'fa-circle-info', colour: '#4f46e5' }
};

/**
 * Does this kind raise the red pop-up? Admin Settings → Alert behaviour
 * decides; the shared list is the fallback until that has been read.
 */
function isUrgentKind(kind, behaviour) {
  const set = behaviour?.[kind]?.urgent;
  return typeof set === 'boolean' ? set : URGENT_NOTIFICATION_KINDS.has(kind);
}

/**
 * Why Ignore is not offered yet, or null. Only a critical alert still waiting
 * for "I've got it" is held back, and only when the admin has not allowed it
 * for that type. The server makes the same check; this just says so up front.
 */
export function ignoreBlockedReason(item, alertsById, behaviour) {
  const alertId = item?.link?.alertId;
  if (!alertId) return null;
  const alert = alertsById?.[alertId];
  if (!alert || (alert.stage !== 'on-call' && alert.stage !== 'everyone')) return null;
  if (behaviour?.[item.kind]?.ignoreBeforeClaim === true) return null;
  return "Waiting for someone to say \"I've got it\" — you can ignore it once it is claimed.";
}

export default function NotificationCentre() {
  const notifications = useNotificationsStore((s) => s.notifications);
  const unread = useNotificationsStore((s) => s.unread);
  const loading = useNotificationsStore((s) => s.loading);
  const error = useNotificationsStore((s) => s.error);
  const panelOpen = useNotificationsStore((s) => s.panelOpen);
  const openPanel = useNotificationsStore((s) => s.openPanel);
  const closePanel = useNotificationsStore((s) => s.closePanel);
  const markRead = useNotificationsStore((s) => s.markRead);
  const markAllRead = useNotificationsStore((s) => s.markAllRead);
  const ignoreItem = useNotificationsStore((s) => s.ignore);
  const startPolling = useNotificationsStore((s) => s.startPolling);
  const loadList = useNotificationsStore((s) => s.loadList);
  const newArrivals = useNotificationsStore((s) => s.newArrivals);
  const setActiveTab = useUiStore((s) => s.setActiveTab);

  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, right: 0 });
  const [approvalId, setApprovalId] = useState(null);
  const [urgent, setUrgent] = useState([]);
  const [pushState, setPushState] = useState('unknown');
  const [pushBusy, setPushBusy] = useState(false);
  const pushStateRef = useRef('unknown');
  useEffect(() => { pushStateRef.current = pushState; }, [pushState]);
  const [alertsOverview, setAlertsOverview] = useState(null);
  // Read inside the polling closure, which would otherwise see a stale value.
  const klaxonRef = useRef(false);
  klaxonRef.current = alertsOverview?.desktopKlaxon === true;
  const behaviour = alertsOverview?.behaviour ?? null;
  const behaviourRef = useRef(null);
  behaviourRef.current = behaviour;
  const [claimingId, setClaimingId] = useState(null);

  const alertsById = {};
  for (const a of alertsOverview?.alerts ?? []) alertsById[a.id] = a;
  const needsSomeone = (alertsOverview?.alerts ?? []).filter(
    (a) => a.youWereTold && (a.stage === 'on-call' || a.stage === 'everyone')
  );

  async function loadAlerts() {
    try {
      setAlertsOverview(await fetchAlerts());
    } catch {
      // Keep the last good copy: a blip must not blank who has what.
    }
  }

  async function claim(alertId) {
    if (!alertId) return;
    setClaimingId(alertId);
    try {
      await claimAlert(alertId);
      showToast('success', "It's yours", 'Everyone else who was told can see you have it, and the escalation has stopped.');
      setUrgent((prev) => prev.filter((n) => n.link?.alertId !== alertId));
    } catch (err) {
      showToast('warning', 'Not claimed', err?.message ?? 'Could not claim this alert.');
    } finally {
      setClaimingId(null);
      await Promise.all([loadAlerts(), loadList({ silent: true })]);
    }
  }

  async function ignore(item) {
    try {
      await ignoreItem(item.id);
      setUrgent((prev) => prev.filter((n) => n.id !== item.id));
    } catch (err) {
      showToast('warning', 'Not ignored', err?.message ?? 'Could not ignore this notification.');
    }
  }

  async function turnOnPush() {
    setPushBusy(true);
    try {
      await enableDesktopPush();
      setPushState('on');
      showToast('success', 'Desktop alerts on', 'Sending a test alert to this browser now.');
      await sendTestPush().catch(() => undefined);
    } catch (err) {
      setPushState(await desktopPushState().catch(() => 'unsupported'));
      showToast('error', 'Could not turn on desktop alerts', err?.message ?? String(err));
    } finally {
      setPushBusy(false);
    }
  }

  async function turnOffPush() {
    setPushBusy(true);
    try {
      await disableDesktopPush();
      setPushState(await desktopPushState().catch(() => 'off'));
    } finally {
      setPushBusy(false);
    }
  }

  /* --- desktop alerts: re-sync this browser's subscription with the server on
         start, then nudge ONCE per session if they are off. Never prompts on
         its own — the browser permission prompt needs a click. --- */
  useEffect(() => {
    let stopped = false;
    (async () => {
      await syncDesktopPush();
      const state = await desktopPushState().catch(() => 'unsupported');
      if (stopped) return;
      setPushState(state);
      let nudged = false;
      try { nudged = sessionStorage.getItem('omni:push-nudged') === '1'; } catch { /* storage blocked */ }
      if (state === 'off' && !nudged) {
        try { sessionStorage.setItem('omni:push-nudged', '1'); } catch { /* storage blocked */ }
        showToast('info', 'Turn on desktop alerts', 'Get P1/P2 tickets, risky users and outages even when Omni is closed.', {
          duration: 15000, actionLabel: 'Turn on', onAction: () => void turnOnPush()
        });
      }
    })();
    return () => { stopped = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* --- arriving from a desktop alert: the service worker either messages this
         tab or opens Omni with ?openTab=… — both land on the right place. --- */
  useEffect(() => {
    function go(tab, id) {
      if (id) void markRead(id);
      if (tab && isValidTab(tab)) setActiveTab(tab);
    }
    const params = new URLSearchParams(window.location.search);
    if (params.has('openTab') || params.has('openNotification')) {
      go(params.get('openTab'), params.get('openNotification'));
      params.delete('openTab');
      params.delete('openNotification');
      const qs = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
    }
    function onMessage(e) {
      if (e.data?.type === 'omni:open-notification') go(e.data.tab, e.data.id);
    }
    navigator.serviceWorker?.addEventListener?.('message', onMessage);
    return () => navigator.serviceWorker?.removeEventListener?.('message', onMessage);
  }, [markRead, setActiveTab]);

  /* --- the count poll --- */
  useEffect(() => startPolling(), [startPolling]);

  /* --- the toast watcher ---
     Polls the list on a slower timer than the count, because it only exists to
     announce arrivals. The store's first read primes the seen-set and announces
     nothing, so opening the app does not toast every unread item at once — the
     silent-baseline rule the Security and Network monitors already follow. */
  useEffect(() => {
    let stopped = false;
    async function check() {
      // Who has each critical alert, so the pop-up and panel stay current
      // when somebody else claims one.
      void loadAlerts();
      const list = await loadList({ silent: true });
      if (stopped || !list) return;
      // Keep an open pop-up current: a ticket picked up in ServiceDesk while
      // it is on screen should say so ("Picked up by …") rather than go stale.
      setUrgent((prev) => (prev.length ? prev.map((n) => list.find((x) => x.id === n.id) ?? n) : prev));
      const arrivals = newArrivals(list);
      const urgentNow = arrivals.filter((n) => isUrgentKind(n.kind, behaviourRef.current));
      if (urgentNow.length > 0) {
        setUrgent((prev) => {
          const have = new Set(prev.map((n) => n.id));
          return [...urgentNow.filter((n) => !have.has(n.id)), ...prev];
        });
        // Admin Settings decides: the short chime, or the red-alert klaxon.
        if (klaxonRef.current) playRedAlert();
        else playAlertChime();
        // Tab in the background and desktop push not set up: fall back to an OS
        // notification from the page itself, so a hidden tab still gets noticed.
        if (document.hidden && pushStateRef.current !== 'on' && 'Notification' in window && Notification.permission === 'granted') {
          for (const n of urgentNow.slice(0, 3)) {
            try { new Notification(n.title, { body: n.body, tag: n.id, requireInteraction: true, icon: '/icons/icon-192.png' }); } catch { /* not allowed here */ }
          }
        }
      }
      for (const item of arrivals.filter((n) => !isUrgentKind(n.kind, behaviourRef.current))) {
        const url = externalUrl(item.link);
        const action = item.link?.type === 'approval' && item.link.id
          ? { duration: 12000, actionLabel: 'Review', onAction: () => setApprovalId(item.link.id) }
          : url
            ? { duration: 12000, actionLabel: 'Open ticket', onAction: () => { void markRead(item.id); window.open(url, '_blank', 'noopener,noreferrer'); } }
            : undefined;
        showToast(
          item.kind === 'approval-rejected' ? 'warning' : item.kind === 'approval-granted' ? 'success' : 'info',
          item.title,
          item.body,
          action
        );
      }
    }
    void check();
    // 30s: urgent alerts are the point of this poll, and the list read is cheap.
    const timer = setInterval(() => void check(), 30_000);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [loadList, newArrivals]);

  /* --- panel positioning: portalled, because the title bar's own stacking
         context would otherwise clip or mis-anchor a fixed panel (the
         PortalDropdown lesson). --- */
  useEffect(() => {
    if (panelOpen) void loadAlerts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panelOpen]);

  useEffect(() => {
    if (!panelOpen) return undefined;
    function place() {
      const rect = btnRef.current?.getBoundingClientRect();
      if (!rect) return;
      setPos({ top: rect.bottom + 8, right: Math.max(8, window.innerWidth - rect.right) });
    }
    place();
    function onDocClick(e) {
      if (
        panelRef.current && !panelRef.current.contains(e.target) &&
        btnRef.current && !btnRef.current.contains(e.target)
      ) closePanel();
    }
    function onKey(e) {
      if (e.key === 'Escape') closePanel();
    }
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [panelOpen, closePanel]);

  function activate(item) {
    if (!item.read) void markRead(item.id);

    // An approval is decided in place: opening a tab and making somebody hunt
    // for the request would defeat the point of notifying them.
    if (item.link?.type === 'approval' && item.link.id) {
      closePanel();
      setApprovalId(item.link.id);
      return;
    }

    const tab = destinationTab(item.link);
    if (tab) {
      closePanel();
      setActiveTab(tab);
      return;
    }

    // No tab, but an external page (a ticket assigned to you): open it.
    const url = externalUrl(item.link);
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  }

  const hasUnread = unread > 0;

  // The button class is `titlebar-icon-btn`, NOT `hamburger-btn`. The latter is
  // `display: none` until the 768px mobile breakpoint — it is the mobile menu
  // button — so anything wearing it is invisible on a desktop screen. That is
  // what hid this icon (and the three feature bells) until 2026-09-08.
  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className="titlebar-icon-btn notif-btn"
        onClick={() => (panelOpen ? closePanel() : openPanel())}
        aria-label={hasUnread ? `${unread} unread notifications` : 'Notifications'}
        aria-expanded={panelOpen}
        title={hasUnread ? `${unread} unread notification${unread === 1 ? '' : 's'}` : 'No unread notifications'}
        style={{ position: 'relative', marginRight: 8 }}
      >
        {/* Outline when empty, solid when not — `far` vs `fas`. Font Awesome is
            self-hosted with the regular style available, so the outline variant
            renders rather than silently falling back to solid. */}
        <i
          className={`${hasUnread ? 'fas' : 'far'} fa-comment-dots${hasUnread ? ' notif-pulse' : ''}`}
          aria-hidden="true"
        />
        {hasUnread && (
          <span
            aria-hidden="true"
            style={{
              position: 'absolute', top: 2, right: 2, minWidth: 16, height: 16, padding: '0 4px',
              borderRadius: 8, background: '#ef4444', color: '#fff', fontSize: 10, fontWeight: 700,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1
            }}
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {panelOpen && createPortal(
        <div
          ref={panelRef}
          role="dialog"
          aria-label="Notifications"
          className="notif-panel"
          style={{ top: pos.top, right: pos.right }}
        >
          <div className="notif-panel-head">
            <span style={{ fontWeight: 700, fontSize: 14 }}>Notifications</span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button
                type="button"
                className="lg-btn lg-btn--glass"
                style={{ padding: '4px 10px', fontSize: 12, opacity: hasUnread ? 1 : 0.5 }}
                disabled={!hasUnread}
                title={hasUnread ? 'Mark everything as read' : 'Nothing unread'}
                onClick={() => void markAllRead()}
              >
                Mark all read
              </button>
              <button
                type="button"
                className="lg-btn lg-btn--glass"
                style={{ padding: '4px 10px', fontSize: 12 }}
                onClick={closePanel}
                aria-label="Close notifications"
              >
                <i className="fas fa-times" aria-hidden="true" />
              </button>
            </div>
          </div>

          <div className="notif-push-row">
            <i className={`fas ${pushState === 'on' ? 'fa-bell' : 'fa-bell-slash'}`} aria-hidden="true"
               style={{ color: pushState === 'on' ? 'var(--ok-strong)' : 'var(--muted)' }} />
            <span className="notif-push-state">{PUSH_STATE_TEXT[pushState] ?? PUSH_STATE_TEXT.unknown}</span>
            {pushState === 'off' && (
              <button type="button" className="lg-btn lg-btn--glass" style={{ padding: '4px 10px', fontSize: 12 }}
                disabled={pushBusy} onClick={() => void turnOnPush()}>
                {pushBusy ? <i className="fas fa-spinner fa-spin" /> : 'Turn on'}
              </button>
            )}
            {pushState === 'on' && (
              <>
                <button type="button" className="lg-btn lg-btn--glass" style={{ padding: '4px 10px', fontSize: 12 }}
                  disabled={pushBusy} title="Send a test alert to this browser"
                  onClick={() => void sendTestPush().then(() => showToast('info', 'Test sent', 'It should appear as a desktop notification in a few seconds.')).catch((e) => showToast('error', 'Test failed', e?.message ?? ''))}>
                  Test
                </button>
                <button type="button" className="lg-btn lg-btn--glass" style={{ padding: '4px 10px', fontSize: 12 }}
                  disabled={pushBusy} onClick={() => void turnOffPush()}>
                  Turn off
                </button>
              </>
            )}
          </div>

          {(() => {
            const line = onCallLine(alertsOverview);
            return line ? (
              <div className="notif-push-row" style={{ borderTop: 'none' }}>
                <i className="fas fa-user-clock" aria-hidden="true" style={{ color: STATUS_COLOUR[line.tone] }} />
                <span className="notif-push-state" style={{ color: line.tone === 'bad' ? STATUS_COLOUR.bad : undefined }}>{line.text}</span>
              </div>
            ) : null;
          })()}

          {needsSomeone.length > 0 && (
            <div className="notif-needs-someone">
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', color: '#b91c1c', marginBottom: 6 }}>
                NEEDS SOMEONE NOW ({needsSomeone.length})
              </div>
              {needsSomeone.map((a) => {
                const status = alertStatusText(a);
                return (
                  <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)' }}>{a.title}</div>
                      {status && <div style={{ fontSize: 12, color: STATUS_COLOUR[status.tone] }}>{status.text}</div>}
                    </div>
                    <button
                      type="button"
                      className="lg-btn lg-btn--glass"
                      style={{ padding: '6px 12px', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}
                      disabled={claimingId === a.id}
                      onClick={() => void claim(a.id)}
                      title="Tell everyone you are dealing with this — it stops the escalation"
                    >
                      {claimingId === a.id ? <i className="fas fa-spinner fa-spin" aria-hidden="true" /> : "I've got it"}
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          <div className="notif-panel-body">
            {loading && notifications.length === 0 && (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--muted-2)' }}>
                <i className="fas fa-spinner fa-spin" /> Loading…
              </div>
            )}

            {error && (
              <div style={{ padding: 14, color: 'var(--bad)', fontSize: 13 }}>
                <i className="fas fa-exclamation-circle" /> {error}
              </div>
            )}

            {!loading && !error && notifications.length === 0 && (
              <div style={{ padding: 28, textAlign: 'center', color: 'var(--muted-2)' }}>
                <i className="far fa-comment-dots" style={{ fontSize: 26, opacity: 0.5 }} />
                <p style={{ margin: '10px 0 0', fontSize: 13 }}>Nothing here yet.</p>
                <p style={{ margin: '4px 0 0', fontSize: 12 }}>
                  Approvals and other things that need you will appear here.
                </p>
              </div>
            )}

            {notifications.map((item) => {
              const meta = KIND_ICON[item.kind] ?? KIND_ICON.info;
              const clickable = (item.link?.type === 'approval' && item.link.id) || Boolean(destinationTab(item.link)) || Boolean(externalUrl(item.link));
              const blocked = ignoreBlockedReason(item, alertsById, behaviour);
              return (
                <div key={item.id} className="notif-row">
                <button
                  type="button"
                  className={`notif-item${item.read ? ' notif-item--read' : ''}`}
                  onClick={() => activate(item)}
                  title={clickable ? 'Open it' : 'Mark as read'}
                >
                  <span className="notif-item-icon" style={{ color: meta.colour }}>
                    <i className={`fas ${meta.icon}`} aria-hidden="true" />
                  </span>
                  <span className="notif-item-text">
                    <span className="notif-item-title">
                      {item.title}
                      {!item.read && <span className="notif-item-dot" aria-label="unread" />}
                    </span>
                    <span className="notif-item-body">{item.body}</span>
                    {(() => {
                      const status = item.link?.alertId ? alertStatusText(alertsById[item.link.alertId]) : null;
                      return status ? (
                        <span className="notif-item-body" style={{ fontWeight: 700, color: STATUS_COLOUR[status.tone] }}>{status.text}</span>
                      ) : null;
                    })()}
                    <span className="notif-item-time">{relativeTime(item.createdAt)}</span>
                  </span>
                  {clickable && <i className="fas fa-chevron-right notif-item-chev" aria-hidden="true" />}
                </button>
                <button
                  type="button"
                  className="notif-ignore"
                  onClick={() => void ignore(item)}
                  disabled={Boolean(blocked)}
                  title={blocked ?? 'Ignore — remove it from your list (nobody else is affected)'}
                  aria-label={`Ignore: ${item.title}`}
                >
                  <i className="fas fa-xmark" aria-hidden="true" />
                </button>
                </div>
              );
            })}
          </div>
        </div>,
        document.body
      )}

      <UrgentAlertModal
        items={urgent}
        klaxon={alertsOverview?.desktopKlaxon === true}
        pushState={pushState}
        onEnablePush={() => void turnOnPush()}
        alertsById={alertsById}
        claimingId={claimingId}
        onClaim={(item) => void claim(item.link?.alertId)}
        onIgnore={(item) => void ignore(item)}
        ignoreBlockedReason={(item) => ignoreBlockedReason(item, alertsById, behaviour)}
        onLater={() => setUrgent([])}
        onAcknowledge={() => {
          for (const n of urgent) void markRead(n.id);
          setUrgent([]);
        }}
        onOpenTab={(item) => {
          void markRead(item.id);
          setUrgent((prev) => prev.filter((n) => n.id !== item.id));
          const tab = destinationTab(item.link);
          if (tab) setActiveTab(tab);
        }}
      />

      <ApprovalModal
        open={Boolean(approvalId)}
        approvalId={approvalId}
        onClose={() => setApprovalId(null)}
        onDecided={() => void loadList({ silent: true })}
      />
    </>
  );
}
